/**
 * GroupJoinLandingModal
 * Shown when a user follows a group invite link.
 * Works for already-logged-in users (direct join) and freshly-logged-in users
 * (invite params are stored in sessionStorage across the login redirect).
 */
import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { invalidateAIVisibilityCache } from '../services/aiVisibilityImpactService';

interface GroupJoinLandingModalProps {
  entityId: string;
  entityType: string;
  clubName: string;
  invitedBy: string;
  onClose: () => void;
  onJoined: (entityType: string) => void;
}

const GroupJoinLandingModal: React.FC<GroupJoinLandingModalProps> = ({
  entityId,
  entityType,
  clubName,
  invitedBy,
  onClose,
  onJoined,
}) => {
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  const [alreadyMember, setAlreadyMember] = useState(false);
  const [inviterName, setInviterName] = useState('');
  const [memberCount, setMemberCount] = useState<number | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (invitedBy) {
      getDoc(doc(db, 'userProfiles', invitedBy))
        .then((snap) => {
          if (snap.exists()) {
            const d = snap.data();
            setInviterName(d.displayName || d.firstName || '');
          }
        })
        .catch(() => {});
    }

    if (entityId) {
      getDocs(
        query(collection(db, 'entityMembers'), where('entityId', '==', entityId))
      )
        .then((snap) => setMemberCount(snap.size))
        .catch(() => {});

      // Check if already a member
      const uid = auth.currentUser?.uid;
      if (uid) {
        getDoc(doc(db, 'entityMembers', `${entityId}_${uid}`))
          .then((snap) => {
            if (snap.exists()) setAlreadyMember(true);
          })
          .catch(() => {});
      }
    }
  }, [invitedBy, entityId]);

  const handleJoin = async () => {
    const user = auth.currentUser;
    if (!user || !entityId) return;

    setJoining(true);
    setError('');

    try {
      const memberDocRef = doc(db, 'entityMembers', `${entityId}_${user.uid}`);
      const memberSnap = await getDoc(memberDocRef);
      if (memberSnap.exists()) {
        setAlreadyMember(true);
        setJoined(true);
        setTimeout(() => onJoined(entityType), 1500);
        return;
      }

      await setDoc(memberDocRef, {
        entityId,
        userId: user.uid,
        role: 'member',
        createdAt: serverTimestamp(),
        createdBy: user.uid,
        joinedViaInviteLink: true,
        invitedBy: invitedBy || null,
      });

      try {
        await invalidateAIVisibilityCache(user.uid);
      } catch {}

      setJoined(true);
      setTimeout(() => onJoined(entityType), 1600);
    } catch {
      setError('Er ging iets mis. Probeer het opnieuw.');
    } finally {
      setJoining(false);
    }
  };

  const typeLabel =
    entityType === 'networkclub'
      ? 'Netwerkclub'
      : entityType === 'mastermind'
      ? 'Mastermind'
      : 'Community';

  const typeEmoji =
    entityType === 'networkclub' ? '🏢' : entityType === 'mastermind' ? '🧠' : '🤝';

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-br from-[#7A9488] to-[#4F6660] p-6 text-white text-center">
          <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3 text-2xl">
            {typeEmoji}
          </div>
          <h2 className="text-xl font-bold leading-tight">{clubName}</h2>
          <p className="text-white/75 text-xs mt-1 font-medium uppercase tracking-wider">
            {typeLabel}
          </p>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <div
            className="rounded-xl p-4 text-center text-sm"
            style={{ backgroundColor: 'rgba(122,148,136,0.07)' }}
          >
            {inviterName ? (
              <p className="text-gray-700">
                <span className="font-semibold">{inviterName}</span> nodigt je uit om lid te
                worden van <span className="font-semibold">{clubName}</span>.
              </p>
            ) : (
              <p className="text-gray-700">
                Je bent uitgenodigd om lid te worden van{' '}
                <span className="font-semibold">{clubName}</span>.
              </p>
            )}
            {memberCount !== null && (
              <p className="text-xs text-gray-500 mt-1.5">
                {memberCount} {memberCount === 1 ? 'lid' : 'leden'} aanwezig
              </p>
            )}
          </div>

          {error && <p className="text-sm text-red-600 text-center">{error}</p>}

          {joined ? (
            <div className="text-center py-2">
              <div className="text-3xl mb-2">{alreadyMember ? '✅' : '🎉'}</div>
              <p className="font-semibold text-[#4F6660]">
                {alreadyMember ? 'Je bent al lid!' : 'Je bent nu lid!'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Je wordt doorgestuurd naar de groepspagina…
              </p>
            </div>
          ) : (
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                Later
              </button>
              <button
                onClick={handleJoin}
                disabled={joining}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white rounded-xl transition-colors disabled:opacity-50"
                style={{ backgroundColor: '#7A9488' }}
                onMouseEnter={(e) => {
                  if (!e.currentTarget.disabled)
                    e.currentTarget.style.backgroundColor = '#4F6660';
                }}
                onMouseLeave={(e) => {
                  if (!e.currentTarget.disabled)
                    e.currentTarget.style.backgroundColor = '#7A9488';
                }}
              >
                {joining ? 'Moment…' : 'Lid worden'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GroupJoinLandingModal;
